import React, { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  InfoOutlined as OverviewIcon,
  PaletteOutlined as BrandingIcon,
  LanguageOutlined as DomainsIcon,
  CodeOutlined as EmbeddingIcon,
  SmartToyOutlined as LlmIcon,
  VpnKeyOutlined as SsoIcon,
  ShieldOutlined as SecurityIcon,
  ContentCopyOutlined as ImportIcon,
  ApiOutlined as ExternalApiIcon,
  MonitorHeartOutlined as LogViewerIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { usePortalDetails } from "./hooks/usePortalDetails";
import type { Portal } from "@/types";
import { ROUTES } from "@/routes/config";
import PageLayout from "@/components/PageLayout";
import OverviewTab from "./sections/OverviewTab";
import BrandingTab from "./sections/BrandingTab";
import DomainsTab from "./sections/DomainsTab";
import EmbeddingTab from "./sections/EmbeddingTab";
import LlmModelsTab from "./sections/LlmModelsTab";
import SsoTab from "./sections/SsoTab";
import SecurityTab from "./sections/SecurityTab";
import ExternalApiTab from "./sections/ExternalApiTab";
import LogViewerTab from "./sections/LogViewerTab";
import ProvisioningStepper from "./sections/ProvisioningStepper";
import ImportContentTab from "@/containers/Portals/portalCopy/ImportContentTab";

const PortalDetails: React.FC = () => {
  const { portalId } = useParams();
  const location = useLocation();
  const initialTab = useMemo(() => {
    const t = (location.state as { tab?: number } | null)?.tab;
    return typeof t === "number" ? t : 0;
  }, [location.state]);
  const [tab, setTab] = useState(initialTab);

  const initialPortal = useMemo<Portal | null>(() => {
    return (location.state as { portal?: Portal } | null)?.portal ?? null;
  }, [location.state]);

  const {
    portal,
    isLoading,
    updatePortal,
    isUpdating,
    retryProvisioning,
    isRetrying,
  } = usePortalDetails(portalId, initialPortal);

  const displayStatus = portal?.status ?? "pending";

  if (!portal) {
    return (
      <PageLayout title="Portal Details" backTo={ROUTES.PORTALS}>
        {isLoading ? (
          <Stack direction="row" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Typography variant="h5" fontWeight={700}>
            Portal not found
          </Typography>
        )}
      </PageLayout>
    );
  }

  const isProvisioning = portal.status === "provisioning" || portal.status === "pending";

  return (
    <PageLayout
      title={portal.name}
      subtitle={
        portal.settings?.custom_domain
          ? portal.settings.custom_domain
          : portal.settings?.subdomain
            ? `${portal.settings.subdomain}.yourdomain.com`
            : portal.slug
      }
      backTo={ROUTES.PORTALS}
      actions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            label={displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
            color={
              displayStatus === "active"
                ? "success"
                : displayStatus === "provisioning"
                  ? "info"
                  : displayStatus === "pending"
                    ? "warning"
                    : displayStatus === "suspended"
                      ? "error"
                      : "default"
            }
            size="small"
          />
        </Stack>
      }
    >
      {/* Provisioning view — shown instead of tabs when not active */}
      {isProvisioning && portal.provisioning_steps && (
        <ProvisioningStepper
          portal={portal}
          onRetry={() => retryProvisioning(portal.id)}
          isRetrying={isRetrying}
        />
      )}

      {/* Fallback for legacy portals without steps */}
      {isProvisioning && !portal.provisioning_steps && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info">
            Provisioning is in progress. This page will update automatically when complete.
          </Alert>
        </Paper>
      )}

      {/* Management view — only when portal is active (or suspended/deleted for viewing) */}
      {!isProvisioning && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <SideNav activeTab={tab} onSelect={setTab} />


          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              minWidth: 0,
            }}
          >
            <Box sx={{ p: 3 }}>
            {tab === 0 && (
              <OverviewTab
                portal={portal}
                isUpdating={isUpdating}
                onSave={(data) =>
                  updatePortal(portal.id, {
                    contact_name: data.contact_name.trim(),
                    contact_email: data.contact_email.trim(),
                    contact_phone: data.contact_phone.trim(),
                  })
                }
                onStatusToggle={(active) =>
                  updatePortal(portal.id, {
                    status: active ? "active" : "inactive",
                  })
                }
              />
            )}

            {tab === 1 && (
              <BrandingTab
                portal={portal}
                isUpdating={isUpdating}
                onSave={(data) =>
                  updatePortal(portal.id, {
                    branding: {
                      logo_url: data.logo_url.trim(),
                      primary_color: data.primary_color.trim(),
                      secondary_color: data.secondary_color.trim(),
                    },
                  })
                }
              />
            )}

            {tab === 2 && (
              <DomainsTab portal={portal} />
            )}

            {tab === 3 && (
              <EmbeddingTab
                portal={portal}
                isUpdating={isUpdating}
                onSave={(data) =>
                  updatePortal(portal.id, {
                    settings: {
                      vision_model: data.vision_model.trim(),
                    },
                  })
                }
              />
            )}

            {tab === 4 && (
              <LlmModelsTab
                key={portal.updated_at ?? portal.id}
                portal={portal}
                isUpdating={isUpdating}
                onSave={(models) =>
                  updatePortal(portal.id, {
                    llm_models: models,
                  })
                }
              />
            )}

            {tab === 5 && (
              <SsoTab portal={portal} />
            )}

            {tab === 6 && (
              <SecurityTab
                portal={portal}
                isUpdating={isUpdating}
                onSave={(data) =>
                  updatePortal(portal.id, {
                    settings: {
                      max_failed_login_attempts: data.max_failed_login_attempts,
                      lockout_duration_minutes: data.lockout_duration_minutes,
                      password_min_length: data.password_min_length,
                      password_require_uppercase: data.password_require_uppercase,
                      password_require_lowercase: data.password_require_lowercase,
                      password_require_number: data.password_require_number,
                      password_require_special: data.password_require_special,
                      password_history_count: data.password_history_count,
                      email_sender_email: data.email_sender_email || null,
                      email_sender_name: data.email_sender_name || null,
                      smtp_host: data.smtp_host || null,
                      smtp_port: data.smtp_port || null,
                      smtp_user: data.smtp_user || null,
                      smtp_password: data.smtp_password || null,
                      password_reset_token_expiry: data.password_reset_token_expiry,
                      mfa_enforced: data.mfa_enforced,
                      mfa_remember_days: data.mfa_remember_days,
                      mfa_app_name: data.mfa_app_name || null,
                    },
                  })
                }
              />
            )}

            {tab === 7 && <ImportContentTab portal={portal} />}

            {tab === 8 && (
              <ExternalApiTab portal={portal} />
            )}

            {tab === 9 && (
              <LogViewerTab portal={portal} />
            )}

            </Box>
          </Paper>
        </Box>
      )}
    </PageLayout>
  );
};

interface NavItem {
  label: string;
  description: string;
  icon: React.ReactElement;
  tabIndex: number;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: "GENERAL",
    items: [
      {
        label: "Overview",
        description: "Contact details & basic info",
        icon: <OverviewIcon fontSize="small" />,
        tabIndex: 0,
      },
    ],
  },
  {
    title: "APPEARANCE",
    items: [
      {
        label: "Branding",
        description: "Logo, colours & identity",
        icon: <BrandingIcon fontSize="small" />,
        tabIndex: 1,
      },
      {
        label: "Domains",
        description: "Custom domain & subdomain",
        icon: <DomainsIcon fontSize="small" />,
        tabIndex: 2,
      },
      {
        label: "Embedding",
        description: "Vision model for images",
        icon: <EmbeddingIcon fontSize="small" />,
        tabIndex: 3,
      },
    ],
  },
  {
    title: "AI",
    items: [
      {
        label: "LLM Models",
        description: "Default & allowed models",
        icon: <LlmIcon fontSize="small" />,
        tabIndex: 4,
      },
    ],
  },
  {
    title: "SECURITY",
    items: [
      {
        label: "SSO",
        description: "Single sign-on providers",
        icon: <SsoIcon fontSize="small" />,
        tabIndex: 5,
      },
      {
        label: "Security",
        description: "MFA, passwords & email",
        icon: <SecurityIcon fontSize="small" />,
        tabIndex: 6,
      },
    ],
  },
  {
    title: "CONTENT",
    items: [
      {
        label: "Import Content",
        description: "Copy agents, CMS & files from another portal",
        icon: <ImportIcon fontSize="small" />,
        tabIndex: 7,
      },
    ],
  },
  {
    title: "INTEGRATIONS",
    items: [
      {
        label: "External API",
        description: "OAuth credentials & access",
        icon: <ExternalApiIcon fontSize="small" />,
        tabIndex: 8,
      },
      {
        label: "Log Viewer",
        description: "Kibana logs & audit trail",
        icon: <LogViewerIcon fontSize="small" />,
        tabIndex: 9,
      },
    ],
  },
];

interface SideNavProps {
  activeTab: number;
  onSelect: (index: number) => void;
}

const SideNav: React.FC<SideNavProps> = ({ activeTab, onSelect }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        "& .MuiListItemButton-root": { my: 0, mx: 0.5 },
      }}
    >
      {NAV_GROUPS.map((group) => {
        const isOpen = !collapsed[group.title];
        const hasActive = group.items.some((i) => i.tabIndex === activeTab);
        return (
          <Box key={group.title} sx={{ mb: 0.25 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                pt: 0.75,
                pb: 0.25,
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() =>
                setCollapsed((prev) => ({
                  ...prev,
                  [group.title]: !prev[group.title],
                }))
              }
            >
              <Typography
                variant="overline"
                sx={{
                  color: hasActive ? "text.primary" : "text.secondary",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  lineHeight: 1,
                }}
              >
                {group.title}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  p: 0.25,
                  transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.15s",
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {group.items.map((item) => {
                  const selected = activeTab === item.tabIndex;
                  return (
                    <ListItemButton
                      key={item.label}
                      selected={selected}
                      onClick={() => onSelect(item.tabIndex)}
                      sx={{ py: 0.5, alignItems: "flex-start" }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, mt: 0.25 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={item.description}
                        primaryTypographyProps={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          lineHeight: 1.15,
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          sx: { mt: 0, lineHeight: 1.2 },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};

export default PortalDetails;
